import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Clock, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/settings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            Cancellation & Fee Policy
          </h1>
          <p className="text-muted-foreground">
            Understanding Bookd's cancellation policies and service fees
          </p>
        </div>

        {/* Service Fee Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Bookd Service Fee
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
              <p className="text-2xl font-bold text-primary">8% Service Fee</p>
              <p className="text-muted-foreground mt-1">
                Applied to all bookings to cover platform operations, payment processing, and customer support.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">What the service fee covers:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 text-sm">
                <li>Secure payment processing</li>
                <li>Platform maintenance and improvements</li>
                <li>Customer support for both Bookers and Bookees</li>
                <li>Dispute resolution services</li>
                <li>Identity verification services</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Cancellation Policies */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Cancellation Policies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-muted-foreground">
              Bookees can choose one of the following cancellation policies for their services. 
              The selected policy will be displayed on their profile and applied to all bookings.
            </p>

            {/* Flexible */}
            <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/5">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-green-500">Flexible</Badge>
                <span className="text-sm text-muted-foreground">Most booker-friendly</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>More than 24 hours before:</strong> Full refund (minus service fee)</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Less than 24 hours before:</strong> 50% refund</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>No-show:</strong> No refund</span>
                </div>
              </div>
            </div>

            {/* Moderate */}
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-amber-500">Moderate</Badge>
                <span className="text-sm text-muted-foreground">Balanced policy</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>More than 48 hours before:</strong> Full refund (minus service fee)</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span><strong>24-48 hours before:</strong> 50% refund</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Less than 24 hours before:</strong> No refund</span>
                </div>
              </div>
            </div>

            {/* Strict */}
            <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/5">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-red-500">Strict</Badge>
                <span className="text-sm text-muted-foreground">Most bookee-friendly</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>More than 7 days before:</strong> Full refund (minus service fee)</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span><strong>2-7 days before:</strong> 50% refund</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Less than 48 hours before:</strong> No refund</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookee Cancellation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              When Bookees Cancel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              If a Bookee cancels an accepted booking, the following applies:
            </p>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium">More than 48 hours before</p>
                <p className="text-sm text-muted-foreground">Full refund to Booker. No penalty to Bookee.</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10">
                <p className="font-medium">24-48 hours before</p>
                <p className="text-sm text-muted-foreground">Full refund to Booker. Warning issued to Bookee.</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/10">
                <p className="font-medium">Less than 24 hours before</p>
                <p className="text-sm text-muted-foreground">
                  Full refund to Booker. Cancellation fee charged to Bookee (15% of booking value). 
                  Repeated last-minute cancellations may result in account suspension.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refund Processing */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Refund Processing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="list-disc list-inside text-muted-foreground space-y-2 text-sm">
              <li>Refunds are processed within 5-10 business days</li>
              <li>The 8% service fee is non-refundable once a booking is confirmed</li>
              <li>Refunds are issued to the original payment method</li>
              <li>In case of disputes, Bookd reserves the right to mediate and make final decisions</li>
            </ul>
          </CardContent>
        </Card>

        {/* Additional Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle>Best Practices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-muted/50">
                <h4 className="font-semibold mb-2">For Bookers</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Review the Bookee's cancellation policy before booking</li>
                  <li>• Communicate any changes as early as possible</li>
                  <li>• Keep records of all communications</li>
                </ul>
              </div>
              <div className="p-4 rounded-xl bg-muted/50">
                <h4 className="font-semibold mb-2">For Bookees</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Set a cancellation policy that works for your business</li>
                  <li>• Honor your commitments whenever possible</li>
                  <li>• Communicate promptly if issues arise</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Last updated: January 2026. Bookd reserves the right to update these policies. 
          Users will be notified of any significant changes.
        </p>
      </div>
    </div>
  );
};

export default CancellationPolicy;
